import type { FC, PropsWithChildren } from 'react';
import styled, { css, useTheme } from 'styled-components';
import type { DataCyProp } from '@/types';
import { TypographySize } from '../enums/size';
import { TypographyType } from '../enums/type';
import type { StaticOptions } from '../types/StaticOptions';
import { getTypographyTypeColor } from '../utils/getTypographyTypeColor';

export interface LabelProps extends DataCyProp {
  size?: TypographySize;
  type?: TypographyType;
  isItalic?: boolean;
  htmlFor?: string;
  className?: string;
  shouldUseDefaultColor?: boolean;
  /**
   * When used in static context, staticOptions must be provided to the component
   *  */
  staticOptions?: StaticOptions;
}

export const StyledLabel = styled.label<Pick<LabelProps, 'size' | 'type' | 'isItalic' | 'shouldUseDefaultColor'>>`
  cursor: inherit;
  margin: 0;
  ${({ theme, size }) => {
    switch (size) {
      case TypographySize.Large:
        return theme.typography.configurations.labelLarge;
      case TypographySize.Small:
        return theme.typography.configurations.labelSmall;
      case TypographySize.Medium:
      default:
        return theme.typography.configurations.labelMedium;
    }
  }};

  ${({ type, theme, shouldUseDefaultColor }) =>
    !shouldUseDefaultColor &&
    css`
    color: ${getTypographyTypeColor(type, theme)};
  `}

  ${({ isItalic: italic }) =>
    italic &&
    css`
      font-style: italic;
    `}
`;

export const Label: FC<PropsWithChildren<LabelProps>> = ({
  children,
  htmlFor,
  className,
  isItalic = false,
  size = TypographySize.Medium,
  type = TypographyType.Normal,
  staticOptions,
  'data-cy': dataCy,
  ...restProps
}) => {
  const defaultTheme = useTheme();
  const { theme } = staticOptions ?? { theme: defaultTheme };
  return (
    <StyledLabel
      className={className}
      data-cy={dataCy}
      htmlFor={htmlFor}
      isItalic={isItalic}
      size={size}
      theme={theme}
      type={type}
      {...restProps}
    >
      {children}
    </StyledLabel>
  );
};
